import { CrispChat } from '@/components/CrispChat'
import { Sidebar } from '@/components/Sidebar'
import { Button } from '@/components/ui/button'
import { ensureBusinessInfoQueryData } from '@/hooks/queries/useBusinessInfo'
import {
  EnsureActiveBusinessWrapper,
  getActiveBusinessId,
} from '@/hooks/useCurrentActiveBusiness'
import { getLoggedInUser } from '@/lib/auth/auth-server-func'
import { authClient } from '@/lib/auth/client'
import * as Sentry from '@sentry/react'
import {
  createFileRoute,
  Navigate,
  Outlet,
  redirect,
  Scripts,
} from '@tanstack/react-router'
import { Component, type ReactNode, useState } from 'react'

const isDev = import.meta.env.DEV

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error boundary caught an error:', error, errorInfo)
    // Log error to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center min-h-screen p-8">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 border border-red-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                Something went wrong
              </h1>
            </div>
            <p className="text-gray-600 mb-4">
              We encountered an unexpected error. Please try refreshing the page
              or contact support if the problem persists.
            </p>
            {isDev && this.state.error && (
              <div className="mt-4 p-3 bg-red-50 rounded border border-red-200">
                <p className="text-xs font-mono text-red-800 break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <Button
              onClick={() => window.location.reload()}
              className="w-full mt-4"
            >
              Refresh Page
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export const Route = createFileRoute('/_dashboard')({
  component: RouteComponent,

  scripts: () => [
    {
      id: 'crisp',
      type: 'text/javascript',
      children: `window.$crisp=[];window.CRISP_WEBSITE_ID="b821bccd-1854-4d8e-9d2a-5a86dc871963";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();`,
    },
  ],
  beforeLoad: async () => {
    const session = await getLoggedInUser()
    if (!session.isLoggedIn) {
      throw redirect({ to: '/signin' })
    }
    await ensureBusinessInfoQueryData()
    const businessId = getActiveBusinessId()
    return {
      businessId,
      auth: session,
    }
  },
})

function RouteComponent() {
  const { data: session, isPending } = authClient.useSession()

  if (session && !session.user.emailVerified && !isDev) {
    return <Navigate to="/verify-email" replace reloadDocument />
  }
  // Handle logout events - redirect to signin if session becomes undefined
  if (!isPending && session?.session === undefined) {
    return <Navigate to="/signin" replace reloadDocument />
  }

  if (isPending) {
    return <div className=""></div>
  }

  return (
    <>
      <CrispChat />
      <EnsureActiveBusinessWrapper>
        <SmallScreenMessage />
        <Sidebar>
          {/* <Navbar /> */}
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Sidebar>
      </EnsureActiveBusinessWrapper>
      <Scripts />
    </>
  )
}

function SmallScreenMessage() {
  const [closed, setClosed] = useState(false)
  if (closed) return null
  return (
    <div className="sm:hidden p-2 bg-white">
      <div className="sm:hidden rounded-xl shadow-md border border-border p-3 flex flex-col items-center justify-center  gap-3">
        <h1 className="text-muted-foreground font-semibold">
          Just a heads up...
        </h1>
        <p className="text-sm text-center">
          Rocket Rank isn't optimized for small screens or touch screens yet!
          Some stuff might not work as expected, so we don't recommend using it
          on your phone.
        </p>
        <Button onClick={() => setClosed(true)}>Got it</Button>
      </div>
    </div>
  )
}
